import { Box, Image } from 'components/ui';
import 'glider-js/glider.min.css';
import { FC, useEffect, useState } from 'react';
import Glider from 'react-glider';
import styled from 'styled-components';
import { isBrowser } from 'utils/helpers';

type SliderImage = {
  src: string;
  alt: string;
};

export interface ImageSliderProps {
  images: SliderImage[];
}

const IMAGE_SIZE = 1000;

const RatioWrapper = styled.div`
  width: 100%;
  height: 0;
  padding-bottom: 100%;
  overflow: hidden;
  isolation: isolate;
`;

const Wrapper = styled(Box)`
  --padding: var(--space-5);

  position: relative;
  max-width: ${IMAGE_SIZE}px;

  .glider-prev,
  .glider-next {
    --size: var(--space-3);
    --offset: calc(var(--size) * -1.25);

    top: 50%;
    width: var(--size);
    height: var(--size);
    color: transparent;
    border: 2px solid var(--color-black);
    transform: translateY(-50%) rotate(45deg);
    transition: opacity 0.25s ease-in-out;

    &.disabled {
      opacity: 0.25;
    }
  }

  .glider-prev {
    left: var(--offset);
    border-top: 0;
    border-right: 0;
  }

  .glider-next {
    right: var(--offset);
    border-bottom: 0;
    border-left: 0;
  }
`;

const ImageSlider: FC<ImageSliderProps> = ({ images }) => {
  const [shouldRender, setShouldRender] = useState(false);

  // Prevent component rendering server side since it uses useLayout effect
  useEffect(() => {
    if (isBrowser) {
      setShouldRender(true);
    }
  }, []);

  if (!shouldRender) {
    return null;
  }

  return (
    <RatioWrapper>
      <Wrapper>
        <Glider
          draggable={true}
          scrollLock={true}
          slidesToScroll={1}
          hasArrows={true}
          dragVelocity={3}
        >
          {images.map(({ src, alt }, index) => (
            <Image
              key={`${src}-${index}`}
              src={src}
              alt={alt}
              width={IMAGE_SIZE}
              height={IMAGE_SIZE}
              objectFit='cover'
            />
          ))}
        </Glider>
      </Wrapper>
    </RatioWrapper>
  );
};

export default ImageSlider;
